import { useMemo, useState, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { ArrowLeftOutlined, MailOutlined, PhoneOutlined, SearchOutlined } from '@ant-design/icons';
import { changelogStore } from '../../entities/changelog/changelog-store';
import { brand } from '@/shared/lib/brand';
import { useMenuModel, type MenuItem } from './Sidebar';
import './mega-menu.css';

/**
 * Меню разделов шапки R3 — в стиле hkeeper.ru: тёмная карточка объекта
 * слева, разделы плитками «иконка + название + что внутри» в две
 * колонки, подпункты — ссылками под описанием. Поиск по разделам —
 * ввод сразу в меню. Пункты, бейджи и гейты — общие с сайдбаром
 * (useMenuModel), здесь только вид.
 */

const NEW_KEYS = new Set(['/ops/today', '/analytics', '/rates-quotas']);

/** Что внутри раздела — одной строкой для человека. */
const ABOUT: Record<string, string> = {
  '/dashboard': 'Обзор отеля и рабочего дня',
  '/ops/today': 'Смена в поле зрения: сроки, уборки, заказы',
  '/analytics': 'Выручка, ADR, RevPAR и загрузка',
  '/schedule': 'Брони по номерам на календаре',
  '/rates-quotas': 'Цены и квоты по каналам продаж',
  '/registrations/bookings': 'Все брони списком',
  '/twin': 'Живая карта отеля',
  concierge: 'Заказы, платежи и гости',
  '/crm': 'Чаты с гостями',
  'reviews-group': 'Отзывы, опросы и NPS',
  '/tasks': 'Поручения и заявки служб',
  housekeeping: 'Уборки, помещения, планировщик',
  conference: 'Залы, мероприятия и заявки',
  registrations: 'Онлайн-регистрация, документы, ключи',
  wifi: 'Авторизация гостей по 152-ФЗ',
  '/site': 'Сайт-визитка отеля',
  settings: 'Объекты, номера, тарифы',
  '/support': 'Обращения в техподдержку',
  system: 'Объекты, расчёты, пользователи',
  marketing: 'База отелей, воронка, рассылки',
  dealer: 'Ваши отели и взаиморасчёты',
};

/** Пастельные подложки иконок — по кругу, как на сайте. */
const TONES = ['blue', 'orange', 'green', 'violet', 'teal', 'rose'] as const;

export interface Tile {
  key: string;
  title: ReactNode;
  text: string;
  icon?: ReactNode;
  href: string;
  links: { key: string; label: ReactNode; text: string }[];
}

const textOf = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(' ');
  const props = (node as any)?.props;
  return props ? textOf(props.children) : '';
};

export function toTiles(items: MenuItem[]): Tile[] {
  const tiles: Tile[] = [];
  for (const it of items) {
    if (!it || !('key' in it) || it.key == null) continue;
    const key = String(it.key);
    const title = ('label' in it ? (it.label as ReactNode) : key) as ReactNode;
    const icon = 'icon' in it ? (it.icon as ReactNode) : undefined;
    const children = ('children' in it ? (it.children as MenuItem[] | undefined) : undefined) ?? [];
    const links = children
      .filter((c) => c && 'key' in c && c.key != null)
      .map((c) => {
        const label = (c as any).label as ReactNode;
        return { key: String((c as any).key), label, text: textOf(label) };
      });
    if (!links.length && !key.startsWith('/')) continue;
    tiles.push({ key, title, icon, text: ABOUT[key] ?? '', href: links[0]?.key ?? key, links });
  }
  return tiles;
}

function moduleState(servicesMap: Record<string, boolean>, platform: any) {
  const svc = (k: string) => servicesMap[k] === true;
  const flag = (k: string) => platform?.[k] === true;
  const all = [
    svc('concierge'), svc('housekeeping'), svc('rep'), svc('online_checkin'),
    flag('schedule_enabled'), flag('channel_manager_enabled'), flag('analytics_enabled'),
    svc('conference'), svc('wifi'), flag('cms_enabled'), flag('twin_enabled'),
  ];
  return { on: all.filter(Boolean).length, total: all.length };
}

export const MegaMenu = observer(function MegaMenu({ onNavigate }: { onNavigate?: () => void }) {
  const m = useMenuModel(onNavigate);
  const [query, setQuery] = useState('');
  const tiles = useMemo(() => toTiles(m.allMenuItems), [m.allMenuItems]);
  const q = query.trim().toLowerCase();
  const shown = q
    ? tiles
        .map((t) => {
          const hit = textOf(t.title).toLowerCase().includes(q) || t.text.toLowerCase().includes(q);
          const links = hit ? t.links : t.links.filter((l) => l.text.toLowerCase().includes(q));
          return hit || links.length ? { ...t, links } : null;
        })
        .filter((t): t is Tile => t !== null)
    : tiles;
  const go = (path: string) => {
    m.navigate(path);
    onNavigate?.();
  };
  const here = m.location.pathname;
  const isHere = (t: Tile) => here === t.key || t.links.some((l) => l.key === here);
  const objectView = !(m.isStaff && !m.isEnterMode);
  const platform = m.platform as any;
  const mods = moduleState(m.servicesMap, platform);
  const news = changelogStore.entries[0];

  return (
    <div className="mm">
      {m.isEnterMode && (
        <div className="mm-enter">
          <button onClick={m.handleExitPlatform}>
            <ArrowLeftOutlined /> {m.isDealer ? 'Мои отели' : 'В систему'}
          </button>
          <span>Вы в объекте как сотрудник HKeeper</span>
        </div>
      )}
      <div className="mm-grid">
        <aside className="mm-card">
          {objectView ? (
            <>
              <div className="mm-eyebrow">ВАШ ОБЪЕКТ</div>
              <div className="mm-hotel">{platform?.name || `Объект #${m.platforms.activePlatformId ?? '—'}`}</div>
              <div className="mm-id">ID {m.platforms.activePlatformId ?? '—'}</div>
              <div className="mm-mods">
                <div className="mm-mods-row">
                  <span>Модули</span>
                  <b>
                    {mods.on} из {mods.total}
                  </b>
                </div>
                <div className="mm-bar" aria-hidden>
                  <i style={{ width: `${Math.round((mods.on / mods.total) * 100)}%` }} />
                </div>
                <p>Шахматка, Channel Manager, аналитика, конференц-залы — подключаются за день.</p>
              </div>
              <button
                className="mm-cta"
                onClick={() => go(`/support?subject=${encodeURIComponent('Подключить модули')}`)}
              >
                Подключить модули →
              </button>
            </>
          ) : (
            <>
              <div className="mm-eyebrow">HKEEPER</div>
              <div className="mm-hotel">Одна платформа для всего отеля</div>
              <p className="mm-card-text">Гости, персонал и руководство — в одной системе.</p>
            </>
          )}
          <button className="mm-news" onClick={() => go('/changelog')}>
            <span>Что нового</span>
            <b>{news ? news.title : 'Лента обновлений'}</b>
          </button>
        </aside>
        <div className="mm-main">
          <div className="mm-head">
            <div className="mm-eyebrow mm-eyebrow-dark">РАЗДЕЛЫ</div>
            <label className="mm-search">
              <SearchOutlined />
              <input
                autoFocus
                placeholder="Найти раздел"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && shown[0]) go(shown[0].links[0]?.key ?? shown[0].href);
                }}
              />
            </label>
          </div>
          <nav className="mm-tiles" aria-label="Разделы кабинета">
            {shown.map((t, i) => (
              <div key={t.key} className={`mm-tile${isHere(t) ? ' on' : ''}`}>
                <button className={`mm-ico tone-${TONES[i % TONES.length]}`} onClick={() => go(t.href)} tabIndex={-1}>
                  {t.icon}
                </button>
                <div className="mm-tile-body">
                  <button className="mm-title" onClick={() => go(t.href)}>
                    {t.title}
                    {NEW_KEYS.has(t.key) && <span className="mm-new">Новое</span>}
                  </button>
                  {t.text && <div className="mm-text">{t.text}</div>}
                  {t.links.length > 0 && (
                    <div className="mm-links">
                      {t.links.map((l) => (
                        <button
                          key={l.key}
                          className={here === l.key ? 'on' : undefined}
                          aria-current={here === l.key ? 'page' : undefined}
                          onClick={() => go(l.key)}
                        >
                          {l.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {!shown.length && <p className="mm-empty">Ничего не нашлось. Попробуйте другое слово.</p>}
          </nav>
          <footer className="mm-foot">
            <span>Техподдержка</span>
            <a href="tel:+74954097578">
              <PhoneOutlined /> +7 (495) 409-75-78
            </a>
            <a href={`mailto:${brand.email}`}>
              <MailOutlined /> {brand.email}
            </a>
            <button onClick={() => go(m.isSuperAdmin ? '/system/support' : '/support')}>Написать в поддержку →</button>
          </footer>
        </div>
      </div>
    </div>
  );
});
